import { useRouter } from 'next/router'

import curriculumAnalytics from '~/analytics/curriculum/curriculumAnalytics'
import VideoCourseIcon from '~/bootcamp/components/VideoCourseIcon'
import { CATEGORY_COLORS } from '~/bootcamp/constants/bootcamp.const'
import { LevelActionType } from '~/bootcamp/types/bootcamp.types'
import SVGImage from '~/components/Image/SVGImage'
import { Link } from '~/components/Link'
import { TopicId, getPathGradientColors } from '~/dashboard/utils/learningPath.utils'
import { useAppDispatch } from '~/redux/hooks'
import { setSelectedGradeActionCreator } from '~/redux/slices/dashboard'
import { parseUrlPathParams } from '~/shared/utils/parseUrlPathParams.utils'
import { LevelSource } from '~/curriculum/constants/curriculum.const'

interface Props {
  iconType?: LevelActionType
  isCompleted: boolean
  levelTitle: string
  topic: TopicId
  path: string
  onClick: (title: string) => void
  isGradient?: boolean
  source?: LevelSource.CURRICULUM | LevelSource.PRE_REQUISITE
}

export default function BootcampLevel({
  iconType = LevelActionType.How,
  levelTitle,
  isCompleted,
  topic,
  path,
  onClick,
  isGradient = false,
  source = LevelSource.CURRICULUM,
}: Props): React.ReactElement {
  const color = getPathGradientColors(topic as TopicId)
  const borderColorClass = isGradient
    ? `${CATEGORY_COLORS[topic]?.gradientClass} p-1`
    : `${CATEGORY_COLORS[topic]?.borderClass} border-4 bg-white`

  const router = useRouter()
  const { grade } = router.query
  const dispatch = useAppDispatch()
  const params = path ? parseUrlPathParams('/bootcamp/:category/:subjectId/:bootcampId/:levelId/:action', path) : {}
  const subjectId = params?.subjectId
  const bootcampId = params?.bootcampId
  const levelId = params?.levelId

  const setSelectedGrade = (): void => {
    if (grade) {
      dispatch(setSelectedGradeActionCreator(parseInt(grade as string, 10)))
      curriculumAnalytics.logGradeLevelClick(
        'bootcamp',
        levelTitle,
        {
          level: levelId as string,
          subjectId: subjectId as string,
          bootcampId: bootcampId as string,
          source,
        },
        'v1',
      )
    }
    onClick(levelTitle)
  }

  return (
    <Link
      className={`w-full ${isCompleted ? 'pt-2.5' : ''}`}
      onClick={setSelectedGrade}
      to={{
        pathname: path,
        query: { showGrade: 'true' },
      }}
    >
      {isCompleted ? (
        <div className="mx-auto relative flex items-center rounded p-2.5 h-fit border-white border bg-purple-darker text-white">
          <SVGImage className="min-w-12" height={34} srcKey="bootcamp/checkmark-white-border.svg" width={34} />
          <div className="absolute -top-3.5 -left-5">
            <VideoCourseIcon className="relative w-11" color={color} iconType={iconType} size="small" />
          </div>
          <p className="text-base tracking-px">{levelTitle}</p>
        </div>
      ) : (
        <div className={`mx-auto rounded p-1  h-fit ${borderColorClass}`}>
          <div className={`flex space-x-2.5 items-center bg-white rounded ${isGradient ? 'px-2' : ''}`}>
            <VideoCourseIcon className="relative w-11" color={color} iconType={iconType} size="small" />
            <p className="text-base tracking-px py-1.5">{levelTitle}</p>
          </div>
        </div>
      )}
    </Link>
  )
}
